import React, { useEffect, useState } from "react";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import { getAppletList, getPromotedModels, updateApplets } from "../ARSServices/ARSServices";
import NoDataComponent from "Components/Common/NoDataComponent";
import AppletCard from "./AppletCard";
import { toast } from "Components/DesignSystem/Toast/useToast";
import { PromotedModelOption } from "../Types";
import { ConfirmationDialog } from 'Pages/GMS/ISR/shared/components/Common/ConfirmationDialog';

type AppletModalComponentProps = {
    showApplet: boolean;
    closeApplet: () => void;
    channelId: string;
    id?: string;
    onSaveSuccess?: () => void;
};

const AppletModal = ({ showApplet, closeApplet, channelId, id, onSaveSuccess }: AppletModalComponentProps) => {

    const [appletData, setAppletData] = useState<AppletResponse[] | null>(null);
    const [originalAppletData, setOriginalAppletData] = useState<AppletResponse[] | null>(null);
    const [promotedModels, setPromotedModels] = useState<PromotedModelOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

    const overlayClasses = 'tw-absolute tw-inset-0 tw-z-40 tw-h-full tw-w-full';
    const menuClasses = showApplet
        ? 'tw-absolute tw-top-0 tw-right-0 tw-h-full tw-max-h-full tw-overflow-auto tw-w-full md:tw-w-[550px] tw-bg-white tw-z-50 tw-shadow-lg'
        : 'tw-hidden';

    const fetchAppletData = async () => {
        setLoading(true);

        try {
            const [appletsRes, modelsRes] = await Promise.all([
                getAppletList(channelId),
                getPromotedModels(channelId)
            ]);

            if (appletsRes?.done) {
                setAppletData(appletsRes?.data);
                setOriginalAppletData(JSON.parse(JSON.stringify(appletsRes?.data)));
            }

            if (modelsRes?.data && modelsRes.data?.done && modelsRes.data?.data) {
                const models: PromotedModelOption[] = modelsRes.data.data.map((model: any) => ({
                    model_id: model.model_id,
                    model_name: model.model_name,
                    type: model.type
                }));
                setPromotedModels(models);
            }
        } catch (error) {
            console.error(`Error fetching data:`, error);
            toast.error("Failed to load applet data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (showApplet) {
            fetchAppletData();
        }
    }, [showApplet, channelId]);

    useEffect(() => {
        if (!appletData || !originalAppletData) {
            setHasChanges(false);
            return;
        }

        const hasAnyChanges = appletData.some((applet, index) => {
            const original = originalAppletData[index];
            if (!original) return false;
            return (
                applet.model_id !== original.model_id ||
                applet.placement_note !== original.placement_note
            );
        });

        setHasChanges(hasAnyChanges);
    }, [appletData, originalAppletData]);

    const handleModelChange = (appletId: string, modelId: string | null, modelName: string) => {
        setAppletData(prev => {
            if (!prev) return prev;

            return prev.map(applet => {
                if (applet.applet_id === appletId && !applet.ab_testing_enabled) {
                    return {
                        ...applet,
                        model_id: modelId || "",
                        model_name: modelName,
                        model: modelId ? [{
                            id: modelId,
                            name: modelName,
                            liveApplet: null
                        }] : []
                    };
                }
                return applet;
            });
        });
    };

    const formatAppletPayload = (applets: AppletResponse[], channelId: string): UpdateAppletPayload => {
        const data: Record<string, FormattedApplet> = {};

        applets.forEach((applet) => {
            const models: FormattedModel[] = applet.model.map((model, index) => {
                const formattedModel: FormattedModel = {
                    id: model.id,
                    is_control: model?.is_control || false,
                    name: model.name,
                };

                if (applet.ab_testing_enabled && model.traffic_distribution) {
                    formattedModel.traffic_distribution = model.traffic_distribution;
                }

                return formattedModel;
            });

            data[applet.applet] = {
                ab_testing_enabled: applet.ab_testing_enabled,
                id: applet.applet_id,
                models,
                name: applet.applet_name,
                placement_note: applet.placement_note || ""
            };
        });

        return {
            data,
            channel_id: channelId
        };
    };

    const handleSave = async () => {
        if (!appletData || !originalAppletData) return;

        setSaving(true);

        try {
            const updatedApplets = appletData.filter((applet, index) => {
                const original = originalAppletData[index];
                if (!original) return false;

                if (applet.ab_testing_enabled) {
                    return applet.placement_note !== original.placement_note;
                }
                return (
                    applet.model_id !== original.model_id ||
                    applet.placement_note !== original.placement_note
                );
            });

            if (updatedApplets.length === 0) {
                toast.info("No changes to save");
                setSaving(false);
                return;
            }

            const formattedPayload = formatAppletPayload(appletData, channelId);
            const response = await updateApplets(formattedPayload);

            if (response?.data?.done) {
                toast.success(`Successfully updated ${updatedApplets.length} applet${updatedApplets.length > 1 ? 's' : ''}`);
                setHasChanges(false);
                onSaveSuccess?.();
                closeApplet();
            } else {
                toast.error(response?.data?.message || "Failed to update applets. Please try again.");
            }
        } catch (error) {
            console.error("Error saving applet models:", error);
            toast.error("Failed to save changes");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        if (hasChanges) {
            setShowConfirmModal(true);
        } else {
            closeApplet();
        }
    };

    const handleConfirmCancel = () => {
        if (originalAppletData) {
            setAppletData(JSON.parse(JSON.stringify(originalAppletData)));
        }
        setHasChanges(false);
        setShowConfirmModal(false);
        closeApplet();
    };

    const handleCancelConfirmModal = () => {
        setShowConfirmModal(false);
    };

    return (
        <div id={id} className="tw-h-[calc(100vh-60px)] tw-fixed tw-top-0 tw-left-0 tw-w-full tw-z-40 tw-mt-16 ">
            <div className={overlayClasses} />
            <div className={menuClasses}>
                <div className="tw-flex tw-justify-between tw-items-center tw-pb-2 tw-pt-2 tw-px-4 tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-border-b tw-border-grey-200">
                    <div className="tw-font-bold tw-text-lg">
                        Applet
                    </div>
                    <div className="tw-flex tw-gap-2">
                        <button
                            className="outline-button"
                            onClick={handleCancelClick}
                            disabled={saving}
                            data-testid="applet-modal-cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="button-style"
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            data-testid="applet-modal-save-btn"
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>

                <div className="tw-p-4">
                    {loading ? (
                        <div className="tw-flex tw-justify-center tw-py-8">
                            <Spinner size="md" label="Loading applets" showLabel data-testid="applet-modal-loading" />
                        </div>
                    ) : appletData && appletData.length > 0 ? (
                        <div>
                            {appletData.map((applet, index) => (
                                <React.Fragment key={applet.applet_id}>
                                    <AppletCard
                                        applet={applet}
                                        setAppletData={setAppletData}
                                        promotedModels={promotedModels}
                                        onModelChange={handleModelChange}
                                    />
                                    {index !== appletData.length - 1 && (
                                        <div className="tw-w-full tw-h-[2px] tw-bg-grey-200 tw-my-4" />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        <NoDataComponent message="No Applets found." />
                    )}
                </div>
            </div>
            <ConfirmationDialog
                isOpen={showConfirmModal}
                title="Discard changes?"
                message='You have unsaved changes. Are you sure you want to cancel?. All unsaved changes will be lost.'
                onConfirm={handleConfirmCancel}
                onCancel={handleCancelConfirmModal}
                confirmText="Discard"
            />
        </div>
    );
};

export default AppletModal;